import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiPagination, ApiResponse, PagedResult, PolicyAcknowledgement } from '../models/staff-module';
import {
  StaffAccountsQuery,
  StaffDepositAccountRow,
  StaffDocumentRequest,
  StaffEmploymentDetails,
  StaffLoanAccountRow,
  StaffProfileDetails,
} from '../models/staff-hr';
import { LeaveRequest } from '../models/staff-module';
import {
  MyAppraisalCounts,
  MyAppraisalRow,
  MyAppraisalsQuery,
  MyCompliance,
  MyDocument,
  MyLeaveBalance,
  MyLeaveCounts,
  MyLeaveRequestCreate,
  MyLeaveRequestsQuery,
  MyLeaveScope,
  MyOverview,
  MyLoanSummary,
  MyDepositSummary,
  MyPermissions,
  MyPhotoResult,
  MyPoliciesQuery,
  MyPolicy,
  MyRelievingOfficer,
} from '../models/my-aquila';
import { StaffModuleError } from './staff-module-api.service';

/**
 * My Aquila endpoints: everything acts on the caller's own staff record (no staff id is ever sent).
 * Document categories, policy categories and appraisal open/create/review stay on the staff-module services.
 */
@Injectable({ providedIn: 'root' })
export class MyAquilaApiService {
  private readonly root = `${environment.baseUrl}${environment.coreUrl}my-aquila`;

  constructor(private http: HttpClient) {}

  // ---- Left panel / read-only tabs ----------------------------------------------------
  /** Own details only (no accounts call): skips the global loading overlay so the page can show its own state. */
  overview(): Observable<MyOverview> {
    return this.http.get<ApiResponse<MyOverview>>(`${this.root}/overview`, { headers: { showLoading: 'false' } })
      .pipe(this.unwrap(), map(r => r.data as MyOverview));
  }
  /** Each summary waits up to 1.2s on the accounts service; `accountsAvailable: false` means no figures. */
  overviewLoans(): Observable<MyLoanSummary> {
    return this.http.get<ApiResponse<MyLoanSummary>>(`${this.root}/overview/loans`, { headers: { showLoading: 'false' } })
      .pipe(this.unwrap(), map(r => r.data as MyLoanSummary));
  }
  overviewDeposits(): Observable<MyDepositSummary> {
    return this.http.get<ApiResponse<MyDepositSummary>>(`${this.root}/overview/deposits`, { headers: { showLoading: 'false' } })
      .pipe(this.unwrap(), map(r => r.data as MyDepositSummary));
  }
  /** jpg/jpeg/png data-URL, max 5MB; takes effect immediately (no approval). */
  updatePhoto(photo: string): Observable<MyPhotoResult> {
    return this.http.put<ApiResponse<MyPhotoResult>>(`${this.root}/photo`, { photo }).pipe(this.unwrap(), map(r => r.data as MyPhotoResult));
  }
  profileDetails(): Observable<StaffProfileDetails> { return this.get<StaffProfileDetails>('/profile-details'); }
  employmentDetails(): Observable<StaffEmploymentDetails> { return this.get<StaffEmploymentDetails>('/employment-details'); }

  // ---- Documents -------------------------------------------------------------------------
  documents(search?: string): Observable<MyDocument[]> {
    return this.http.get<ApiResponse<MyDocument[]>>(`${this.root}/documents`, { params: this.params({ search }) }).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  addDocument(body: StaffDocumentRequest): Observable<MyDocument> {
    return this.http.post<ApiResponse<MyDocument>>(`${this.root}/documents`, body).pipe(this.unwrap(), map(r => r.data as MyDocument));
  }
  /** 403 for documents the caller didn't upload (`editable` false). */
  updateDocument(id: number, body: StaffDocumentRequest): Observable<MyDocument> {
    return this.http.put<ApiResponse<MyDocument>>(`${this.root}/documents/${id}`, body).pipe(this.unwrap(), map(r => r.data as MyDocument));
  }
  deleteDocument(id: number): Observable<void> {
    return this.http.delete<ApiResponse>(`${this.root}/documents/${id}`).pipe(this.unwrap(), map(() => undefined));
  }
  /** Every document id exactly once. */
  reorderDocuments(ids: number[]): Observable<MyDocument[]> {
    return this.http.put<ApiResponse<MyDocument[]>>(`${this.root}/documents/order`, { ids }).pipe(this.unwrap(), map(r => r.data ?? []));
  }

  // ---- Performance -----------------------------------------------------------------------
  appraisals(query: MyAppraisalsQuery = {}): Observable<PagedResult<MyAppraisalRow>> { return this.paged<MyAppraisalRow>('/appraisals', query); }
  appraisalCounts(): Observable<MyAppraisalCounts> { return this.get<MyAppraisalCounts>('/appraisals/counts'); }

  // ---- Accounts --------------------------------------------------------------------------
  loanAccounts(query: StaffAccountsQuery = {}): Observable<PagedResult<StaffLoanAccountRow>> { return this.paged<StaffLoanAccountRow>('/loan-accounts', query); }
  depositAccounts(query: StaffAccountsQuery = {}): Observable<PagedResult<StaffDepositAccountRow>> { return this.paged<StaffDepositAccountRow>('/deposit-accounts', query); }

  // ---- Leave -----------------------------------------------------------------------------
  leaveRequests(query: MyLeaveRequestsQuery = {}): Observable<PagedResult<LeaveRequest>> { return this.paged<LeaveRequest>('/leave/requests', query); }
  leaveCounts(scope: MyLeaveScope = 'ALL'): Observable<MyLeaveCounts> { return this.get<MyLeaveCounts>('/leave/counts', { scope }); }
  leaveBalances(year?: number): Observable<MyLeaveBalance[]> {
    return this.http.get<ApiResponse<MyLeaveBalance[]>>(`${this.root}/leave/balances`, { params: this.params({ year }) }).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  relievingOfficers(search?: string): Observable<MyRelievingOfficer[]> {
    return this.http.get<ApiResponse<MyRelievingOfficer[]>>(`${this.root}/leave/relieving-officers`, { params: this.params({ search }) }).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** Staff on a higher salary grade than the caller (50 at most). Empty = no reviewer needed. Optional dropdown, so no error toast. */
  leaveReviewers(search?: string): Observable<MyRelievingOfficer[]> {
    return this.http.get<ApiResponse<MyRelievingOfficer[]>>(`${this.root}/leave/reviewers`, { params: this.params({ search }), headers: { 'suppress-error-toast': 'true' } }).pipe(this.unwrap(), map(r => r.data ?? []));
  }
  /** The caller must be the request's chosen reviewer. */
  approveLeaveRequest(id: number, note?: string): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests/${id}/approve`, note ? { note } : {}).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  rejectLeaveRequest(id: number, note?: string): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests/${id}/reject`, note ? { note } : {}).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  leaveRequest(id: number): Observable<LeaveRequest> { return this.get<LeaveRequest>(`/leave/requests/${id}`); }
  /** Always filed for the caller. The apply dialog shows failures inline, so the global error toast is suppressed. */
  createLeaveRequest(body: MyLeaveRequestCreate): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests`, body, { headers: { 'suppress-error-toast': 'true' } }).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }
  cancelLeaveRequest(id: number, note?: string): Observable<LeaveRequest> {
    return this.http.post<ApiResponse<LeaveRequest>>(`${this.root}/leave/requests/${id}/cancel`, note ? { note } : {}).pipe(this.unwrap(), map(r => r.data as LeaveRequest));
  }

  // ---- Policies --------------------------------------------------------------------------
  policies(query: MyPoliciesQuery = {}): Observable<PagedResult<MyPolicy>> { return this.paged<MyPolicy>('/policies', query); }
  policy(id: number): Observable<MyPolicy> { return this.get<MyPolicy>(`/policies/${id}`); }
  acknowledgePolicy(id: number): Observable<PolicyAcknowledgement> {
    return this.http.post<ApiResponse<PolicyAcknowledgement>>(`${this.root}/policies/${id}/acknowledge`, {}).pipe(this.unwrap(), map(r => r.data as PolicyAcknowledgement));
  }
  compliance(): Observable<MyCompliance> { return this.get<MyCompliance>('/policies/compliance'); }

  // ---- Permissions -----------------------------------------------------------------------
  permissions(): Observable<MyPermissions> { return this.get<MyPermissions>('/permissions'); }
  /** Quiet variant for the app-wide permission check: no global loader, no error toast. */
  permissionsQuiet(): Observable<MyPermissions> {
    return this.http.get<ApiResponse<MyPermissions>>(`${this.root}/permissions`, { headers: { showLoading: 'false', 'suppress-error-toast': 'true' } })
      .pipe(this.unwrap(), map(r => r.data as MyPermissions));
  }

  // ---- helpers ----------------------------------------------------------------------------
  private get<T>(path: string, query: object = {}): Observable<T> {
    return this.http.get<ApiResponse<T>>(`${this.root}${path}`, { params: this.params(query) }).pipe(this.unwrap(), map(r => r.data as T));
  }

  private paged<T>(path: string, query: object): Observable<PagedResult<T>> {
    return this.http.get<ApiResponse<T[]>>(`${this.root}${path}`, { params: this.params(query) }).pipe(
      this.unwrap(),
      map(r => ({ data: r.data ?? [], pagination: r.pagination as ApiPagination })),
    );
  }

  private params(query: object): HttpParams {
    let params = new HttpParams();
    Object.entries(query || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params = params.set(key, String(value));
    });
    return params;
  }

  private unwrap<R extends ApiResponse<any>>() {
    return (source: Observable<R>): Observable<R> => source.pipe(
      map(res => {
        if (res && res.successful === false) throw this.toError({ status: res.code, error: res });
        return res;
      }),
      catchError(err => throwError(() => (err instanceof HttpErrorResponse ? this.toError(err) : err))),
    );
  }

  private toError(err: any): StaffModuleError {
    const body = err?.error;
    const fieldErrors = body && typeof body.data === 'object' && body.data !== null && !Array.isArray(body.data)
      ? body.data as Record<string, string>
      : undefined;
    const message = body?.message
      || (err?.status === 0 ? 'Unable to reach the server. Please check your connection and try again.' : err?.message)
      || 'Something went wrong. Please try again.';
    return { status: err?.status ?? 0, message, fieldErrors };
  }
}
